import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { normalizeRole } from '../../constants/permissions';
import { useAuth } from '../../contexts/AuthContext';
import { useRBAC } from '../../utils/rbac';
import Forbidden from '../common/Forbidden';

/**
 * RequireRole Component
 *
 * Ensures the authenticated user has the specified role(s) before rendering children.
 * Uses the RBAC system for role checking with hierarchy support.
 *
 * @param {string|string[]} roles - Required role(s)
 * @param {boolean} requireAll - Whether all roles are required (default: false - any role)
 * @param {boolean} useHierarchy - Whether to use role hierarchy (default: false)
 * @param {React.Component} fallback - Custom fallback component (default: Forbidden)
 * @param {React.ReactNode} children - Children to render if role access granted
 */
export function RequireRole({
  roles,
  requireAll = false,
  useHierarchy = false,
  fallback = <Forbidden />,
  children,
}) {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  const rbac = useRBAC(user);

  // Show loading state while auth is initializing
  if (loading) {
    return (
      <div className='min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8'>
        <div className='sm:mx-auto sm:w-full sm:max-w-md'>
          <div className='text-center'>
            <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto'></div>
            <p className='mt-4 text-sm text-gray-600'>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to='/login' state={{ from: location }} replace />;
  }

  // Check role access
  const requiredRoles = Array.isArray(roles) ? roles : [roles];
  let hasAccess = false;

  if (useHierarchy) {
    // Use role hierarchy - user needs minimum role level
    const minRole = requiredRoles[0]; // For hierarchy, we typically check against one minimum role
    hasAccess = rbac.hasMinimumRole(minRole);
  } else {
    // Check exact role match
    if (requireAll) {
      hasAccess = requiredRoles.every(role => rbac.hasRole(role));
    } else {
      hasAccess = requiredRoles.some(role => rbac.hasRole(role));
    }
  }

  // Render fallback if no role access
  if (!hasAccess) {
    return fallback;
  }

  // Render children if role access granted
  return children;
}

/**
 * Higher-order component for role-based access
 * @param {string|string[]} requiredRoles - Required role(s)
 * @param {boolean} requireAll - Whether all roles required
 * @param {boolean} useHierarchy - Whether to use role hierarchy
 * @param {React.Component} fallback - Fallback component
 * @returns {function} HOC function
 */
export function withRole(
  requiredRoles = [],
  requireAll = false,
  useHierarchy = false,
  fallback = undefined // Use undefined to allow RequireRole's default
) {
  return function RoleProtectedComponent(Component) {
    return function WrappedComponent(props) {
      return (
        <RequireRole
          roles={requiredRoles}
          requireAll={requireAll}
          useHierarchy={useHierarchy}
          fallback={fallback}
        >
          <Component {...props} />
        </RequireRole>
      );
    };
  };
}

/**
 * Enhanced Permissions Hook
 * Provides comprehensive permission checking using RBAC system
 */
export function usePermissions() {
  const { user } = useAuth();
  const rbac = useRBAC(user);

  return {
    // Role checking
    hasRole: rbac.hasRole.bind(rbac),
    hasAnyRole: roles =>
      Array.isArray(roles)
        ? roles.some(role => rbac.hasRole(role))
        : rbac.hasRole(roles),
    hasAllRoles: roles =>
      Array.isArray(roles)
        ? roles.every(role => rbac.hasRole(role))
        : rbac.hasRole(roles),
    hasMinimumRole: rbac.hasMinimumRole.bind(rbac),

    // Permission checking
    hasPermission: rbac.hasPermission.bind(rbac),
    hasAnyPermission: rbac.hasAnyPermission.bind(rbac),
    hasAllPermissions: rbac.hasAllPermissions.bind(rbac),

    // Resource access checking
    canAccess: rbac.canAccess.bind(rbac),
    canEdit: rbac.canEdit.bind(rbac),
    canDelete: rbac.canDelete.bind(rbac),

    // User management
    canManageUser: rbac.canManageUser.bind(rbac),
    // Only admins may write to /api/v1/users (backend role policy); anyone
    // may edit their own profile through /api/v1/users/me.
    canEditUser: targetUser => {
      if (!user || !targetUser) return false;
      if (rbac.hasRole('admin')) return true;
      return user.id === targetUser.id;
    },
    canDeleteUser: targetUser => rbac.canManageUser(targetUser),

    // Convenience methods
    canManageUsers: () => rbac.hasRole('admin'),
    canManageSystem: () => rbac.hasRole('admin'),
    canViewReports: () => rbac.hasMinimumRole('manager'),
    canManageHITL: () => rbac.hasMinimumRole('manager'),
    canConfigureSystem: () => rbac.hasRole('admin'),

    // User info
    userRole: normalizeRole(user?.roles?.[0] || user?.role),
    userRoles: user?.roles || (user?.role ? [user?.role] : []),
    userPermissions: rbac.getPermissions(),
    isAdmin: rbac.hasRole('admin'),
    isManager: rbac.hasRole('manager'),
    isViewer: rbac.hasRole('viewer'),

    // RBAC manager instance for advanced usage
    rbac,
  };
}
